import Vue from 'vue';

import _ from 'lodash';

import { getFields } from '_/components/Dashboard/Widgets/ItemStatsWidget/ItemStatsWidget.fields.js';

import { mapGetters, mapActions } from 'vuex';

import { userHasRole } from '_/vuex/actions/userActions';

export default {
    /**
     * Define props
     *
     * @type  {Object}
     */
    props: {
        widget: {
            required: true,
            type: Object,
        },
    },

    /**
     * Set the component data
     *
     * @return  {object}
     */
    data() {
        return {
            /**
             * Stores widget setup fields
             *
             * @type  {Array}
             */
            fields: [],

            /**
             * Is polling active?
             *
             * @type  {boolean}
             */
            polling: false,

            /**
             * Animation interval id
             *
             * @type  {boolean}
             */
            animationTimeout: false,

            /**
             * Toggles the widget setup mode
             *
             * @type  {boolean}
             */
            setupMode: _.isEmpty(this.widget.module),

            /**
             * Source module data
             *
             * @type  {Object}
             */
            sourceModule: {
                /**
                 * Souce module fields
                 *
                 * @type  {Array}
                 */
                fields: [],

                lastHourItemsCount: null,
                lastHourStatus: '',
                todayItemsCount: null,
                todayStatus: '',
                currentWeekItemsCount: null,
                currentWeekStatus: '',
                currentMonthItemsCount: null,
                currentMonthStatus: '',
                totalItemsCount: null,
            },

            /**
             * An object sent to child components; used to signal that the fields need to be reset, with the ability
             * to limit the reset to only certain fields
             *
             * @type  {Object}
             */
            formFieldsReset: {
                /**
                 * A list of field names to include in a reset action
                 *
                 * @type  {Array}
                 */
                includeFields: [],

                /**
                 * A parameter that is listened by the child components. Changed via moment().valueOf() method to always
                 * set the fresh value.
                 *
                 * @type  {integer}
                 */
                resetData: null,
            },
        };
    },

    /**
     * Set the computed properties
     *
     * @type {object}
     */
    computed: {
        ...mapGetters({
            photonModule: 'photonModule/photonModule',
        }),

        /**
         * Sets the widget theme CSS class
         *
         * @return  {string}
         */
        getWidgetTheme() {
            return this.widget.theme ? this.widget.theme : 'panel-primary';
        },

        /**
         * prepares the module dropdown data
         *
         * @return  {array}
         */
        moduleOptions() {
            return this.photonModule.modules.map((module) => {
                return {
                    id: module.table_name,
                    text: module.name,
                };
            });
        },

        /**
         * Unpacks the meta_data JSON to object
         *
         * @return  {object}
         */
        metaData() {
            const languageCode = this.ui.defaultLocale.languageCode;
            return JSON.parse(this.widget.meta_data[languageCode]);
        },
    },

    /**
     * Set the methods
     *
     * @type  {Object}
     */
    methods: {
        ...mapActions('widget', [
            'deleteEntry',
            'getItemStats',
            'getSelectedModuleFields',
            'submitEntry',
            'updateWidget',
        ]),

        /**
         * Toggles the setup mode
         *
         * @return  {void}
         */
        toggleSetup() {
            if (this.polling) {
                clearTimeout(this.polling);
            }

            if (this.animationTimeout) {
                clearTimeout(this.animationTimeout);
            }

            if (this.setupMode) {
                let payload = {
                    ...this.widget,
                };

                this.submitEntry({ payload }).then((response) => {
                    this.updateWidget({ widget: response.entry }).then(() => {
                        this.setupMode = false;

                        setTimeout(() => {
                            this.getModuleData(true);
                        }, 300);
                    });
                });
            } else {
                $(this.$el).find('.animated.flash').removeClass('animated flash');

                this.setupMode = true;
            }
        },

        /**
         * Removes the widget
         *
         * @return  {void}
         */
        removeWidget() {
            this.deleteEntry({ entryId: this.widget.id }).then(() => {
                if (this.polling) {
                    clearTimeout(this.polling);
                }
            });
        },

        /**
         * Gets the latest module entries
         *
         * @param   {boolean}  longpoll
         * @return  {void}
         */
        getModuleData(longpoll) {
            if (!this.widget.module) {
                this.sourceModule = {};

                return;
            }

            this.getItemStats({
                moduleTableName: this.widget.module,
            }).then((stats) => {
                Vue.set(this.sourceModule, 'lastHourItemsCount', stats.last_hour_count);
                Vue.set(this.sourceModule, 'lastHourStatus', stats.last_hour_status);
                Vue.set(this.sourceModule, 'todayItemsCount', stats.today_count);
                Vue.set(this.sourceModule, 'todayStatus', stats.today_status);
                Vue.set(this.sourceModule, 'currentWeekItemsCount', stats.current_week_count);
                Vue.set(this.sourceModule, 'currentWeekStatus', stats.current_week_status);
                Vue.set(this.sourceModule, 'currentMonthItemsCount', stats.current_month_count);
                Vue.set(this.sourceModule, 'currentMonthStatus', stats.current_month_status);
                Vue.set(this.sourceModule, 'totalItemsCount', stats.total);

                if (this.polling) {
                    clearTimeout(this.polling);
                }

                $(this.$el).find('li:first-child').removeClass('animated flash');
                if (
                    stats.last_hour_status !== '' ||
                    stats.today_status !== '' ||
                    stats.current_week_status !== '' ||
                    stats.current_month_status !== ''
                ) {
                    setTimeout(() => {
                        $(this.$el).find('li:first-child').addClass('animated flash');
                    }, 200);
                }

                const refreshInterval = parseInt(this.widget.refresh_interval, 10);

                if (refreshInterval && longpoll) {
                    this.polling = setTimeout(() => {
                        this.getModuleData(longpoll);
                    }, refreshInterval);
                }
            });
        },

        /**
         * Checks if a user has given role
         *
         * @param   {string}  role
         * @return  {bool}
         */
        userHasRole,
    },

    /**
     * Set the mounted hook
     *
     * @return  {void}
     */
    mounted() {
        this.$nextTick(() => {
            this.getSelectedModuleFields({ moduleTableName: this.widget.module }).then((fields) => {
                this.sourceModule.fields = fields;

                this.fields = getFields(this);

                const longpoll = !this.widget.setupMode;

                this.getModuleData(longpoll);
            });
        });
    },

    /**
     * Define watched properties
     *
     * @type  {Object}
     */
    watch: {
        widget: {
            deep: true,
            handler(newEntry) {
                this.getSelectedModuleFields({ moduleTableName: newEntry.module }).then((fields) => {
                    Vue.set(this.sourceModule, 'fields', fields);

                    this.fields = getFields(this);

                    this.formFieldsReset.resetData = moment().valueOf();
                });
            },
        },
    },

    /**
     * Set the beforeDestroy hook
     *
     * @return  {void}
     */
    beforeDestroy() {
        if (this.polling) {
            clearTimeout(this.polling);
        }

        if (this.animationTimeout) {
            clearTimeout(this.animationTimeout);
        }
    },
};
